import * as THREE from 'three';
import { Engine } from './core/engine';
import { InputManager } from './core/input';
import { GameClock } from './core/clock';
import { AudioManager } from './core/audio';
import { World } from './world/world';
import { Sky } from './world/sky';
import { generateTextureAtlas } from './world/texture-generator';
import { Player } from './entities/player';
import { Mob } from './entities/mob';
import { ZOMBIE_DEF } from './entities/mobs/zombie';
import { SKELETON_DEF } from './entities/mobs/skeleton';
import { PIG_DEF } from './entities/mobs/pig';
import { SHEEP_DEF } from './entities/mobs/sheep';
import { COW_DEF } from './entities/mobs/cow';
import { CHICKEN_DEF } from './entities/mobs/chicken';
import { Inventory } from './inventory/inventory';
import { getMiningSpeed, getTool } from './inventory/tools';
import { UIManager, UICallbacks } from './ui/ui-manager';
import { NetworkClient } from './network/client';
import { BlockId } from '@shared/block-ids';
import { getBlock } from './world/block-registry';
import { TICK_RATE, HOTBAR_SLOTS, BLOCK_REACH } from './constants';
import { GameScreen, GameSettings, ToolType } from './types';
import { getBiome } from './world/biome';
import { getTemperature, getHumidity, getHeight } from './generation/surface';
import { NoiseGenerator } from './generation/noise';
import { seedRandom } from './utils/math';
import { initDB, saveWorld, getWorlds, savePlayerState, loadPlayerState } from './storage/local-db';

export class Game {
  private engine: Engine;
  private input: InputManager;
  private clock: GameClock;
  private audio: AudioManager;
  private ui: UIManager;
  private network: NetworkClient;

  private world: World | null = null;
  private sky: Sky | null = null;
  private player: Player | null = null;
  private inventory: Inventory | null = null;
  private mobs: Mob[] = [];
  private atlasTexture: THREE.Texture | null = null;
  private atlasCanvas: HTMLCanvasElement | null = null;

  // Block highlight
  private highlightMesh: THREE.LineSegments;
  private breakProgress = 0;
  private breakingBlock: { x: number; y: number; z: number } | null = null;
  private breakOverlay: THREE.Mesh | null = null;
  private placeCooldown = 0;

  // Mob spawning
  private mobSpawnTimer = 0;
  private readonly MAX_MOBS = 20;

  // Survival
  private gameMode: 'survival' | 'creative' = 'survival';
  private playerHealth = 20;
  private playerMaxHealth = 20;
  private playerHunger = 20;
  private playerMaxHunger = 20;
  private hungerTimer = 0;
  private hungerDrainRate = 0.1; // per second
  private damageTimer = 0;
  private regenTimer = 0;

  // World persistence
  private currentWorldId: string | null = null;
  private _pendingWorldName: string | null = null;

  // Multiplayer server list
  private serverList: { id: string; name: string; players: number; maxPlayers: number; seed: number }[] = [];

  private running = false;
  private animFrameId = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.engine = new Engine(canvas);
    this.input = new InputManager(canvas);
    this.clock = new GameClock(TICK_RATE);
    this.audio = new AudioManager();
    this.network = new NetworkClient();

    // Block highlight wireframe
    const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(1.005, 1.005, 1.005));
    this.highlightMesh = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 2 }));
    this.highlightMesh.visible = false;
    this.engine.scene.add(this.highlightMesh);

    // Break overlay (black transparent overlay on block being mined)
    const breakGeo = new THREE.BoxGeometry(1.01, 1.01, 1.01);
    const breakMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.breakOverlay = new THREE.Mesh(breakGeo, breakMat);
    this.breakOverlay.visible = false;
    this.engine.scene.add(this.breakOverlay);

    // Generate texture atlas
    this.atlasTexture = generateTextureAtlas();
    this.atlasCanvas = (this.atlasTexture.image as HTMLCanvasElement);

    const callbacks: UICallbacks = {
      onPlayWorld: (id, name, seed, mode) => {
        this._pendingWorldName = name;
        this.startGame(seed, mode ?? 'survival', id ?? undefined);
      },
      onJoinServer: (address) => this.joinServer(address),
      onCreateServer: (name, maxPlayers) => this.createServer(name, maxPlayers),
      onSettingsChanged: (settings) => this.applySettings(settings),
      onResume: () => this.resumeGame(),
      onQuitToMenu: () => this.quitToMenu(),
    };

    this.ui = new UIManager(callbacks);
    this.ui.setAtlasCanvas(this.atlasCanvas);
    this.ui.setServerList(this.serverList);
    this.ui.show(GameScreen.MAIN_MENU);
  }

  private async startGame(seed: number, mode: 'survival' | 'creative', worldId?: string): Promise<void> {
    this.ui.show(GameScreen.LOADING);
    this.gameMode = mode;

    // Cleanup previous game
    this.cleanup();

    // Init DB
    await initDB();

    // Create or reuse world ID
    this.currentWorldId = worldId ?? ('world-' + Date.now().toString(36));

    // Create world
    this.world = new World(this.engine.scene, seed, this.atlasTexture!);
    this.world.worldId = this.currentWorldId;

    // Create inventory (empty for survival, full for creative)
    this.inventory = new Inventory(mode === 'creative');
    this.ui.setInventory(this.inventory);

    // Create player
    this.player = new Player(this.engine.camera, this.input, 0, 100, 0);

    // Create sky
    this.sky = new Sky(this.engine.scene, this.engine.directionalLight, this.engine.ambientLight);

    // Reset survival stats
    this.playerHealth = this.playerMaxHealth;
    this.playerHunger = this.playerMaxHunger;
    this.hungerTimer = 0;

    // Try to load saved player state
    const savedState = await loadPlayerState(this.currentWorldId) as any;
    if (savedState) {
      this.playerHealth = savedState.health ?? this.playerMaxHealth;
      this.playerHunger = savedState.hunger ?? this.playerMaxHunger;
      if (savedState.inventory && this.inventory) {
        this.inventory.slots = savedState.inventory;
        this.inventory.selectedSlot = savedState.selectedSlot ?? 0;
      }
    }

    // Initial chunk loading
    this.ui.updateLoadingProgress(10, 'Generating terrain...');

    const startX = savedState?.x ?? 0;
    const startZ = savedState?.z ?? 0;

    for (let i = 0; i < 30; i++) {
      this.world.updateChunks(startX, startZ);
      this.ui.updateLoadingProgress(10 + (i / 30) * 80, `Loading chunks... ${Math.floor((i / 30) * 100)}%`);
      await new Promise(r => setTimeout(r, 5));
    }

    if (savedState?.x !== undefined) {
      this.player.setPosition(savedState.x, savedState.y, savedState.z);
    } else {
      const spawnY = this.world.getSpawnHeight(0, 0);
      this.player.setPosition(0.5, spawnY + 1, 0.5);
    }

    this.ui.updateLoadingProgress(100, 'Done!');
    await new Promise(r => setTimeout(r, 200));

    // Save world info (name is stored via onPlayWorld callback)
    await saveWorld({
      id: this.currentWorldId,
      name: this._pendingWorldName ?? 'World',
      seed,
      createdAt: savedState?.createdAt ?? Date.now(),
      lastPlayed: Date.now(),
      gameMode: mode,
    });
    this._pendingWorldName = null;

    this.ui.show(GameScreen.PLAYING);
    this.audio.init();

    if (!this.running) {
      this.running = true;
      this.clock.start();
      this.gameLoop();
    }
  }

  private joinServer(serverId: string): void {
    const server = this.serverList.find(s => s.id === serverId);
    const seed = server?.seed ?? Math.random() * 999999 | 0;
    // In a real app, this would connect via WebSocket. For now, start local game with server's seed.
    this.startGame(seed, 'survival');
  }

  private createServer(name: string, maxPlayers: number): void {
    const seed = Math.random() * 999999 | 0;
    const id = 'srv-' + Math.random().toString(36).slice(2, 8);
    this.serverList.push({
      id,
      name,
      players: 1,
      maxPlayers,
      seed,
    });
    this.ui.setServerList(this.serverList);
    // Join the newly created server
    this.startGame(seed, 'survival');
  }

  private applySettings(settings: GameSettings): void {
    this.engine.updateSettings(settings);
  }

  private resumeGame(): void {
    this.ui.show(GameScreen.PLAYING);
    this.input.clearMouseButtons();
    if (this.input.isMobile) this.input.touch.enable();
  }

  private async quitToMenu(): Promise<void> {
    await this.saveGameState();
    this.cleanup();
    this.ui.show(GameScreen.MAIN_MENU);
  }

  private async saveGameState(): Promise<void> {
    if (!this.currentWorldId || !this.player) return;
    // Save player state
    await savePlayerState(this.currentWorldId, {
      x: this.player.body.x,
      y: this.player.body.y,
      z: this.player.body.z,
      health: this.playerHealth,
      hunger: this.playerHunger,
      inventory: this.inventory?.slots ?? [],
      selectedSlot: this.inventory?.selectedSlot ?? 0,
      createdAt: Date.now(),
    });
    // Save modified chunks
    if (this.world) {
      await this.world.saveModifiedChunks();
    }
  }

  private cleanup(): void {
    if (this.world) {
      this.world.dispose();
      this.world = null;
    }
    if (this.sky) {
      this.sky.dispose();
      this.sky = null;
    }
    for (const mob of this.mobs) {
      this.engine.scene.remove(mob.mesh);
    }
    this.mobs = [];
    this.player = null;
    this.inventory = null;
    this.breakProgress = 0;
    this.breakingBlock = null;
  }

  private gameLoop(): void {
    if (!this.running) return;

    const dt = this.clock.update();

    while (this.clock.consumeFixedStep()) {
      this.fixedUpdate(this.clock.fixedDt);
    }

    this.renderFrame(dt);
    this.animFrameId = requestAnimationFrame(() => this.gameLoop());
  }

  private fixedUpdate(dt: number): void {
    if (!this.world || !this.player) return;

    const screen = this.ui.screen;
    this.handleMenuInput();
    if (screen !== GameScreen.PLAYING) return;

    const isSolid = (x: number, y: number, z: number) => this.world!.isSolid(x, y, z);
    this.player.update(dt, isSolid);

    this.handleBlockInteraction(dt);

    // Hotbar
    if (this.inventory) {
      const scroll = this.input.getScrollDelta();
      if (scroll !== 0) {
        this.inventory.scrollSlot(scroll);
        this.ui.refreshHotbar();
      }
      for (let i = 0; i < HOTBAR_SLOTS; i++) {
        if (this.input.isKeyDown(`Digit${i + 1}`)) {
          this.inventory.selectSlot(i);
          this.ui.refreshHotbar();
        }
      }
    }

    this.world.updateChunks(this.player.body.x, this.player.body.z);
    this.world.tickSave(dt);
    this.updateMobs(dt);

    this.mobSpawnTimer -= dt;
    if (this.mobSpawnTimer <= 0) {
      this.spawnMobs();
      this.mobSpawnTimer = 5;
    }

    if (this.damageTimer > 0) this.damageTimer -= dt;

    // Survival mechanics
    if (this.gameMode === 'survival') {
      this.updateSurvival(dt);
    }

    // Fall into void
    if (this.player.body.y < -10) {
      this.playerHealth = 0;
    }
  }

  private updateSurvival(dt: number): void {
    // Hunger drain
    this.hungerTimer += dt;
    if (this.hungerTimer >= 1) {
      this.hungerTimer -= 1;
      this.playerHunger = Math.max(0, this.playerHunger - this.hungerDrainRate);
    }

    // Health regen when well-fed
    if (this.playerHunger >= 18 && this.playerHealth < this.playerMaxHealth) {
      this.regenTimer += dt;
      if (this.regenTimer >= 2) {
        this.regenTimer -= 2;
        this.playerHealth = Math.min(this.playerMaxHealth, this.playerHealth + 1);
      }
    }

    // Starvation damage
    if (this.playerHunger <= 0) {
      this.hungerTimer += dt;
      if (this.hungerTimer >= 4) {
        this.hungerTimer -= 4;
        this.playerHealth = Math.max(1, this.playerHealth - 1);
      }
    }
  }

  private handleMenuInput(): void {
    const screen = this.ui.screen;

    if (this.input.isKeyDown('Escape')) {
      this.input.releasePointerLock();
      if (screen === GameScreen.PLAYING) {
        if (this.input.isMobile) this.input.touch.disable();
        this.ui.show(GameScreen.PAUSED);
      } else if (screen === GameScreen.PAUSED) {
        this.resumeGame();
      } else if (screen === GameScreen.INVENTORY) {
        this.ui.returnCraftingItems();
        if (this.input.isMobile) this.input.touch.enable();
        this.ui.show(GameScreen.PLAYING);
      }
      // Clear the key so it doesn't re-trigger
      this.input.touch.virtualKeys.delete('Escape');
      return;
    }

    if (screen === GameScreen.PLAYING) {
      if (this.input.isKeyDown('KeyE')) {
        this.input.releasePointerLock();
        if (this.input.isMobile) this.input.touch.disable();
        this.ui.show(GameScreen.INVENTORY);
        this.input.touch.virtualKeys.delete('KeyE');
      }
      if (this.input.isKeyDown('F3')) {
        this.ui.toggleDebug();
      }
    }
  }

  private handleBlockInteraction(dt: number): void {
    if (!this.world || !this.player) return;
    if (!this.input.isPointerLocked && !this.input.isMobile) return;

    this.placeCooldown -= dt;
    const target = this.player.targetBlock;

    // Update highlight
    if (target) {
      this.highlightMesh.visible = true;
      this.highlightMesh.position.set(target.x + 0.5, target.y + 0.5, target.z + 0.5);
    } else {
      this.highlightMesh.visible = false;
    }

    // Break block (left click - hold to mine in survival)
    if (this.input.isMouseDown(0) && target) {
      const blockId = this.world.getBlock(target.x, target.y, target.z);
      if (blockId !== BlockId.AIR && blockId !== BlockId.BEDROCK) {
        const blockDef = getBlock(blockId);

        if (this.gameMode === 'creative') {
          // Instant break in creative
          if (this.breakProgress === 0 || !this.breakingBlock ||
              this.breakingBlock.x !== target.x || this.breakingBlock.y !== target.y || this.breakingBlock.z !== target.z) {
            this.world.setBlock(target.x, target.y, target.z, BlockId.AIR);
            this.breakProgress = 0;
            this.breakingBlock = null;
          }
        } else {
          // Survival: progressive mining
          if (!this.breakingBlock ||
              this.breakingBlock.x !== target.x || this.breakingBlock.y !== target.y || this.breakingBlock.z !== target.z) {
            // Changed target, reset progress
            this.breakingBlock = { x: target.x, y: target.y, z: target.z };
            this.breakProgress = 0;
          }

          // Mining speed (hardness determines time, tool speeds it up)
          const hardness = blockDef.hardness <= 0 ? 0.1 : blockDef.hardness;
          const item = this.inventory?.getSelectedItem();
          const toolSpeed = getMiningSpeed(item?.toolId, blockDef.toolType);
          const mineTime = (hardness * 1.5) / toolSpeed;
          this.breakProgress += dt / mineTime;

          // Show break overlay
          if (this.breakOverlay) {
            this.breakOverlay.visible = true;
            this.breakOverlay.position.set(target.x + 0.5, target.y + 0.5, target.z + 0.5);
            (this.breakOverlay.material as THREE.MeshBasicMaterial).opacity = this.breakProgress * 0.5;
          }

          if (this.breakProgress >= 1) {
            this.world.setBlock(target.x, target.y, target.z, BlockId.AIR);
            const dropId = blockDef.drops ?? blockId;
            if (this.inventory) {
              this.inventory.addItem(dropId);
              // Drain tool durability
              const selectedItem = this.inventory.getSelectedItem();
              if (selectedItem?.toolId && selectedItem.durability !== undefined) {
                selectedItem.durability--;
                if (selectedItem.durability <= 0) {
                  this.inventory.removeFromSelected(1);
                  this.playBreakSound();
                }
              }
              this.ui.refreshHotbar();
            }
            // Play block break sound
            this.playBreakSound();
            // Hunger drain from mining
            this.playerHunger = Math.max(0, this.playerHunger - 0.025);
            this.breakProgress = 0;
            this.breakingBlock = null;
            if (this.breakOverlay) this.breakOverlay.visible = false;
          }
        }
      }
    } else {
      // Not holding left click - reset break progress
      if (this.breakProgress > 0) {
        this.breakProgress = 0;
        this.breakingBlock = null;
        if (this.breakOverlay) this.breakOverlay.visible = false;
      }
    }

    // Right-click crafting table to open crafting
    if (this.input.isMouseDown(2) && target && this.placeCooldown <= 0) {
      const clickedBlock = this.world.getBlock(target.x, target.y, target.z);
      if (clickedBlock === BlockId.CRAFTING_TABLE) {
        this.input.releasePointerLock();
        this.ui.openCraftingTable();
        this.placeCooldown = 0.5;
        return;
      }
    }

    // Place block (right click)
    if (this.input.isMouseDown(2) && target && this.placeCooldown <= 0) {
      const placeX = target.x + target.normal[0];
      const placeY = target.y + target.normal[1];
      const placeZ = target.z + target.normal[2];

      const px = Math.floor(this.player.body.x);
      const py = Math.floor(this.player.body.y);
      const pz = Math.floor(this.player.body.z);
      if (placeX === px && placeZ === pz && (placeY === py || placeY === py + 1)) {
        return;
      }

      if (this.inventory) {
        const item = this.inventory.getSelectedItem();
        if (item) {
          this.world.setBlock(placeX, placeY, placeZ, item.id);
          if (this.gameMode === 'survival') {
            this.inventory.removeFromSelected(1);
          }
          this.ui.refreshHotbar();
          this.playPlaceSound();
          this.placeCooldown = 0.25;
        }
      }
    }
  }

  private updateMobs(dt: number): void {
    if (!this.world || !this.player) return;

    const isSolid = (x: number, y: number, z: number) => this.world!.isSolid(x, y, z);
    const px = this.player.body.x;
    const py = this.player.body.y;
    const pz = this.player.body.z;

    for (let i = this.mobs.length - 1; i >= 0; i--) {
      const mob = this.mobs[i];

      if (mob.dead || mob.distanceTo(px, py, pz) > 80) {
        // Collect drops when mob dies (not when despawning)
        if (mob.dead && this.inventory) {
          for (const drop of mob.getDrops()) {
            if (drop.count > 0) {
              this.inventory.addItem(drop.id, drop.count);
            }
          }
          this.ui.refreshHotbar();
        }
        this.engine.scene.remove(mob.mesh);
        this.mobs.splice(i, 1);
        continue;
      }

      mob.update(dt, isSolid, px, py, pz);

      if (mob.canAttack(px, py, pz) && this.damageTimer <= 0 && this.gameMode === 'survival') {
        this.playerHealth = Math.max(0, this.playerHealth - mob.def.attackDamage);
        this.damageTimer = 1;
        this.playDamageSound();
      }
    }

    // Player attack mobs
    if (this.input.isMouseDown(0) && (this.input.isPointerLocked || this.input.isMobile)) {
      const dir = this.player.lookDirection;
      const eye = this.player.eyePosition;

      for (const mob of this.mobs) {
        if (mob.dead) continue;
        const dist = mob.distanceTo(eye.x, eye.y, eye.z);
        if (dist < BLOCK_REACH) {
          const dx = mob.body.x - eye.x;
          const dy = (mob.body.y + mob.body.height / 2) - eye.y;
          const dz = mob.body.z - eye.z;
          const dot = dx * dir.x + dy * dir.y + dz * dir.z;
          if (dot > 0 && dot < BLOCK_REACH) {
            // Use tool attack damage if holding a tool
            const heldItem = this.inventory?.getSelectedItem();
            let attackDmg = 4;
            if (heldItem?.toolId) {
              const tool = getTool(heldItem.toolId);
              if (tool) attackDmg = tool.attackDamage;
            }
            mob.damage(attackDmg);
            const kbDir = new THREE.Vector3(dx, 0, dz).normalize();
            mob.body.vx = kbDir.x * 5;
            mob.body.vz = kbDir.z * 5;
            mob.body.vy = 3;
            this.playHitSound();
            break;
          }
        }
      }
    }
  }

  private spawnMobs(): void {
    if (!this.world || !this.player) return;
    if (this.mobs.length >= this.MAX_MOBS) return;

    const px = this.player.body.x;
    const pz = this.player.body.z;
    const rng = seedRandom(performance.now() | 0);

    const angle = rng() * Math.PI * 2;
    const dist = 20 + rng() * 30;
    const sx = Math.floor(px + Math.cos(angle) * dist);
    const sz = Math.floor(pz + Math.sin(angle) * dist);

    const sy = this.world.getSpawnHeight(sx, sz);
    if (sy <= 0 || sy > 200) return;
    if (!this.world.isSolid(sx, sy - 1, sz)) return;
    if (this.world.isSolid(sx, sy, sz)) return;
    if (this.world.isSolid(sx, sy + 1, sz)) return;

    const isNight = this.sky ? (this.sky.time > 0.75 || this.sky.time < 0.25) : false;
    const mobDefs = isNight
      ? [ZOMBIE_DEF, SKELETON_DEF, PIG_DEF, SHEEP_DEF, COW_DEF]
      : [PIG_DEF, SHEEP_DEF, COW_DEF, CHICKEN_DEF, PIG_DEF, SHEEP_DEF];

    const def = mobDefs[Math.floor(rng() * mobDefs.length)];
    const mob = new Mob(def, sx + 0.5, sy, sz + 0.5);
    this.mobs.push(mob);
    this.engine.scene.add(mob.mesh);
  }

  private renderFrame(dt: number): void {
    if (!this.world || !this.player) {
      this.engine.render();
      return;
    }

    if (this.sky) {
      this.sky.update(dt, this.engine.camera.position);
    }

    const noise = new NoiseGenerator(this.world.seed);
    const temp = getTemperature(noise, this.player.body.x, this.player.body.z);
    const humid = getHumidity(noise, this.player.body.x, this.player.body.z);
    const height = getHeight(noise, this.player.body.x, this.player.body.z);
    const biome = getBiome(temp, humid, height);

    this.ui.updateHUD({
      selectedSlot: this.inventory?.selectedSlot ?? 0,
      fps: this.clock.fps,
      x: this.player.body.x,
      y: this.player.body.y,
      z: this.player.body.z,
      chunkCount: this.world.getLoadedChunkCount(),
      biome: biome.name,
      timeOfDay: this.sky?.time ?? 0,
      health: this.playerHealth,
      maxHealth: this.playerMaxHealth,
      hunger: this.playerHunger,
      maxHunger: this.playerMaxHunger,
      gameMode: this.gameMode,
      breakProgress: this.breakProgress,
    });

    this.engine.render();
  }

  // Procedural sound effects using Web Audio API
  private playBreakSound(): void {
    this.audio.resume();
    const ctx = this.audio.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(200 + Math.random() * 100, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
  }

  private playPlaceSound(): void {
    this.audio.resume();
    const ctx = this.audio.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(300 + Math.random() * 50, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.1);
  }

  private playHitSound(): void {
    this.audio.resume();
    const ctx = this.audio.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(150 + Math.random() * 80, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  }

  private playDamageSound(): void {
    this.audio.resume();
    const ctx = this.audio.ctx;
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.2);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
  }

  dispose(): void {
    this.running = false;
    cancelAnimationFrame(this.animFrameId);
    this.cleanup();
    this.engine.dispose();
    this.input.dispose();
    this.network.disconnect();
  }
}
